import React, { useState, useEffect, useMemo } from 'react'; // <-- Import useMemo
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { fetchData } from '../api/publicService';
import { Helmet } from 'react-helmet-async';
import {
  Box, Typography, Container, CircularProgress, Alert, Grid, Paper, AppBar,
  Toolbar, Accordion, AccordionSummary, AccordionDetails, List,
  ListItem, ListItemIcon, ListItemText, Slide, Divider, ImageList, ImageListItem,
  Snackbar, Rating as MuiRating, Button
} from '@mui/material';
import axios from 'axios';

// --- SLICK CAROUSEL IMPORTS ---
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// --- ICON IMPORTS ---
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LoyaltyTwoTone from '@mui/icons-material/LoyaltyTwoTone';
import HotTubTwoTone from '@mui/icons-material/HotTubTwoTone';

// --- REUSABLE COMPONENT IMPORTS ---
import InlineDestinationCard from './InlineDestinationCard';
import RelatedItemCard from '../components/RelatedItemCard';
import Rating from '../components/Rating';
import SubTrip from './RelatedTrips';

// --- DEFINE THE BRAND COLOR PALETTE ---
const colors = {
  primary: '#005F69',
  primaryAccent: '#F28C28',
  secondaryAccent: '#B9935A',
  text: '#7B3F00',
  background: '#F1F1F1',
};

// --- HELPER FUNCTION FOR CURRENCY ---
const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode?.toLowerCase()) {
    case 'eur': return '€';
    case 'gbp': return '£';
    case 'usd': default: return '$';
  }
};

const TripDetail = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { currency } = useCurrency();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [userRating, setUserRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const heroSliderSettings = {
    dots: true, fade: true, infinite: true, speed: 1000, slidesToShow: 1,
    slidesToScroll: 1, autoplay: true, autoplaySpeed: 5000, arrows: false, pauseOnHover: true,
  };

  const loadTripDetails = async () => {
    try {
      setLoading(true);
      const data = await fetchData(`/trips/${slug}`, language);
      setTrip(data);
      setError(null);
    } catch (err) {
      setError('Could not find the requested tour.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTripDetails();
  }, [slug, language]);

  const allJourneyImages = useMemo(() => {
    if (!trip) return [];
    const tripImages = trip.images || [];
    const destinationImages = trip.itinerary.flatMap(day => 
      day.destination?.images || []
    );
    const combinedImages = [...tripImages, ...destinationImages];
    return [...new Set(combinedImages)];
  }, [trip]);

  const handleRatingSubmit = async (newValue) => {
    if (!newValue) return;
    setUserRating(newValue);
    try {
      await axios.put(`https://bella-ceylon.onrender.com/api/public/ratings/trip/${trip._id}`, { star: newValue });
      setSnackbar({ open: true, message: 'Thank you for your feedback!' });
      setRatingSubmitted(true);
      loadTripDetails();
    } catch (error) {
      console.error("Failed to submit rating", error);
      setSnackbar({ open: true, message: 'Failed to submit rating.' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: colors.background }}><CircularProgress size={60} sx={{ color: colors.primary }} /></Box>;
  if (error || !trip) return <Container sx={{ py: 5 }}><Alert severity="error">{error || 'Trip data could not be loaded.'}</Alert></Container>;

  const price = trip.basePrice?.[currency] || trip.basePrice?.usd || 0;
  const symbol = getCurrencySymbol(currency);
  
  // --- *** THE FIX IS HERE *** ---
  // Remove the hardcoded limit of 8 images. .slice(1) gets ALL images from the second one to the end.
  const galleryImagesForDisplay = allJourneyImages.slice(1);

  const SectionTitle = ({ children }) => (
    <>
      <Typography variant="h4" align="center" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: colors.text, mb: 2 }}>
        {children}
      </Typography>
      <Divider sx={{ my: 3, mx: 'auto', width: '80px', borderWidth: '1px', borderColor: colors.secondaryAccent }} />
    </>
  );

  return (
    <Box sx={{ bgcolor: '#FFF' }}>
      <Helmet>
        <title>{trip.title} | Bella Ceylon Voyages</title>
        <meta name="description" content={trip.summary} />
      </Helmet>

      {/* --- Section 1: Hero Carousel --- */}
      {allJourneyImages.length > 0 && (
        <Box sx={{ height: '80vh', position: 'relative', display: 'flex', alignItems: 'center', color: 'white', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 60%)', zIndex: 1, }, '.slick-dots': { bottom: '25px', 'li button:before': { fontSize: '12px', color: '#fff', opacity: 0.6, }, 'li.slick-active button:before': { opacity: 1, color: colors.primaryAccent, }, }, }}>
          <Slider {...heroSliderSettings} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            {allJourneyImages.map((image, index) => (
              <Box key={index} sx={{ width: '100%', height: '80vh' }}>
                <Box component="img" src={image} alt={`${trip.title} - view ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ))}
          </Slider>
          <Container sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Slide direction="down" in={!loading} timeout={800}>
              <Typography variant="h2" component="h1" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}>{trip.title}</Typography>
            </Slide>
          </Container>
        </Box>
      )}

      {/* --- Section 2: Sticky Info Bar --- */}
      <AppBar position="sticky" sx={{ top: 0, bgcolor: '#FFFFFF', color: colors.text, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderBottom: `1px solid ${colors.background}` }}>
        <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', py: 1 }}>
              <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarMonthTwoToneIcon sx={{ color: colors.primary }} /><Typography fontWeight="500"><strong>{trip.duration}</strong> Days</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LoyaltyTwoTone sx={{ color: colors.primary }} /><Typography fontWeight="500"><strong>{trip.tripType?.name}</strong></Typography></Box>
                <Rating value={trip.totalRatings} text={`${trip.ratings.length} reviews`} color={colors.secondaryAccent} />
              </Box>
              <Box sx={{ textAlign: 'right', ml: 'auto' }}>
                <Typography variant="caption" sx={{ color: colors.text, opacity: 0.8 }}>From</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: colors.primaryAccent }}>{symbol}{price.toLocaleString()} / {trip.pricePer}</Typography>
              </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* --- Section 3: Main Content (ITINERARY) --- */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={5}>
          {/* --- CODE QUALITY FIX: Correct MUI Grid props --- */}
          <Grid item size={{ xs: 12, md: 8 }}>
            <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: colors.text, mb: 3 }}>Your Day-by-Day Journey</Typography>
            {trip.itinerary.map((day, index) => (
              <Accordion key={day.day} defaultExpanded={index === 0} sx={{ mb: 1.5, boxShadow: 'none', border: `1px solid ${colors.secondaryAccent}22`, borderRadius: '8px', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.primary }} />} sx={{ '&.Mui-expanded': { borderBottom: `1px solid ${colors.secondaryAccent}22` } }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: colors.primaryAccent }}>Day {day.day}: <Typography component="span" variant="h6" sx={{ color: colors.text, fontWeight: 400 }}>{day.dayTitle}</Typography></Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: {xs: 2, sm: 3}, bgcolor: `${colors.primary}05` }}>
                  <Typography color={colors.text} sx={{ lineHeight: 1.7, mb: 1 }}>{day.dayDescription}</Typography>
                  {day.destination && <InlineDestinationCard destination={day.destination} />}
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    {/* --- CODE QUALITY FIX: Correct MUI Grid props --- */}
                    {day.accommodation && <Grid item size={{ xs: 12, sm: 6 }}><Box sx={{display: 'flex', alignItems: 'center'}}><HotTubTwoTone sx={{color: colors.secondaryAccent, mr: 1.5}}/><ListItemText primaryTypographyProps={{fontWeight: 'bold', color: colors.text}} secondaryTypographyProps={{color: colors.text, opacity: 0.9}} primary="Accommodation" secondary={day.accommodation} /></Box></Grid>}
                    <Grid item size={{ xs: 12, sm: 6 }}><Box sx={{display: 'flex', alignItems: 'center'}}><RestaurantIcon sx={{color: colors.secondaryAccent, mr: 1.5}}/><ListItemText primaryTypographyProps={{fontWeight: 'bold', color: colors.text}} secondaryTypographyProps={{color: colors.text, opacity: 0.9}} primary="Meals Included" secondary={Object.keys(day.meals).filter(m => day.meals[m]).join(', ') || 'None'} /></Box></Grid>
                  </Grid>
                  {day.activities?.length > 0 && (<>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1, color: colors.text }}>Key Activities:</Typography>
                    <List dense>{day.activities.map((act, i) => (<ListItem key={i}><ListItemIcon sx={{minWidth: 32}}><LocalActivityIcon fontSize="small" sx={{color: colors.secondaryAccent}} /></ListItemIcon><ListItemText primary={act} primaryTypographyProps={{color: colors.text, opacity: 0.9}} /></ListItem>))}</List>
                  </>)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
          
          {/* --- CODE QUALITY FIX: Correct MUI Grid props --- */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: '8px', position: 'sticky', top: 100, border: `1px solid ${colors.secondaryAccent}44`, bgcolor: '#FFF' }}>
              <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: colors.text, mb: 2 }}>Tour Overview</Typography>
              <Typography variant="body1" sx={{ color: colors.text, opacity: 0.9, mb: 3 }}>{trip.summary}</Typography>
              <Divider sx={{ my: 3, borderColor: `${colors.secondaryAccent}44` }} />
              <Typography variant="h6" fontWeight="bold" sx={{color: colors.text}} gutterBottom>What's Included</Typography>
              <List dense>{trip.inclusions.map((item, i) => (<ListItem key={i}><ListItemIcon sx={{minWidth: 32}}><CheckCircleOutlineIcon sx={{ color: colors.primary }} /></ListItemIcon><ListItemText primary={item} primaryTypographyProps={{color: colors.text, opacity: 0.9}} /></ListItem>))}</List>
              <Typography variant="h6" fontWeight="bold" sx={{color: colors.text, mt: 2}} gutterBottom>What's Not Included</Typography>
              <List dense>{trip.exclusions.map((item, i) => (<ListItem key={i}><ListItemIcon sx={{minWidth: 32}}><HighlightOffIcon sx={{ color: colors.text, opacity: 0.7 }} /></ListItemIcon><ListItemText primary={item} primaryTypographyProps={{color: colors.text, opacity: 0.9}} /></ListItem>))}</List>
              <Divider sx={{ my: 3, borderColor: `${colors.secondaryAccent}44` }} />
              <Box sx={{ textAlign: 'center', my: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{color: colors.text}}>Rate This Tour</Typography>
                <MuiRating name="trip-rating" value={userRating} onChange={(e, val) => handleRatingSubmit(val)} size="large" disabled={ratingSubmitted} sx={{ color: colors.secondaryAccent }} emptyIcon={<StarBorderIcon style={{ color: colors.secondaryAccent, opacity: 0.5 }} fontSize="inherit" />} />
                {ratingSubmitted && <Typography variant="caption" display="block" color={colors.primary} mt={1}>Thank you!</Typography>}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      {/* --- Section 4: Image Gallery (UPDATED) --- */}
      {galleryImagesForDisplay.length > 0 && (
        <Box sx={{ bgcolor: colors.background, py: 8 }}>
          <Container maxWidth="lg">
            <SectionTitle>A Glimpse of Your Journey</SectionTitle>
            <ImageList variant="quilted" cols={4} rowHeight={200} gap={8}>
              {galleryImagesForDisplay.map((item, index) => (
                <ImageListItem key={item} cols={index === 0 ? 2 : 1} rows={index === 0 ? 2 : 1}>
                  <img src={item} alt={`Gallery view ${index + 1}`} loading="lazy" style={{ borderRadius: '8px', width: '100%', height: '100%', objectFit: 'cover' }} />
                </ImageListItem>
              ))}
            </ImageList>
          </Container>
        </Box>
      )}

      <Box sx={{ bgcolor: colors.background }}>
          <Container maxWidth="lg">
            <SubTrip />
          </Container>
        </Box>

      {/* Other sections like Related Trips would go here */}
      
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ open: false, message: '' })} message={snackbar.message} />
    </Box>
  );
};

export default TripDetail;